import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
//import useLocalStorage from '../../hooks/useLocalStorage';
import { appMode } from '@utils/url';
import { TLoginRes } from '@api/auth.api';
import { createTransform } from 'redux-persist';

export interface User {
  token: string;
  sessionID: string;
  userID: string;
  email: string;
  position: string;
  name: string;
  shortName: string;
  birthdate: string;
  avatar: string;
  roleLevel: number;
  pass?: string;
  isServoAccount: boolean;
}

export interface ISystemDialog {
  _id?: number;
  message: string;
  dialog_type: 'Dialog Box' | 'Notification Message';
}

export interface UserLogin {
  token: string;
  isMaintenanceMode: number;
  systemDialogs: ISystemDialog[];
  user: {
    name: string;
    email: string;
    id: string;
    shortName?: string;
    roleLevel?: number;
    isServoAccount?: boolean;
  };
  device: {
    verified: boolean;
    verificationCode: string;
    deviceID: string;
  };
  mfa?: {
    require: boolean;
    token: string;
  };
  sessionID: string;
  logInProcess: string;
  usersID?: string;
  userID?: string;
  verificationCode: string;
}

export interface UserState {
  info: User;
  isLoggedIn: boolean;
  loginInfo: UserLogin;
  mfaLogin: {
    open: boolean;
    mfaLoginToken: string;
    signature: string;
    loading: boolean;
    loginToken: string;
    data?: TLoginRes['data'];
    isMFAEnabled?: boolean;
  };
}

export const initialState: UserState = {
  info: {
    token: '',
    sessionID: '',
    userID: '',
    email: '',
    position: '',
    name: '',
    shortName: '',
    birthdate: moment(new Date()).format('YYYY-MM-DD'),
    avatar: '',
    pass: '',
    roleLevel: 0,
    isServoAccount: false,
  },
  isLoggedIn: false,
  loginInfo: {
    token: '',
    isMaintenanceMode: 0,
    systemDialogs: [],
    user: {
      name: '',
      email: '',
      id: '',
    },
    device: {
      verified: false,
      verificationCode: '',
      deviceID: '',
    },
    sessionID: '',
    logInProcess: '',
    verificationCode: '',
  },
  mfaLogin: {
    open: false,
    mfaLoginToken: '',
    signature: '',
    loading: false,
    loginToken: '',
    data: undefined,
    isMFAEnabled: false,
  },
};

export const saveUserThunk = createAsyncThunk(
  'user/saveUser',
  async (data: UserLogin) => {
    // simulate a delay (like an API call)
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const isServoAccount =
      data?.user?.isServoAccount ??
      (appMode === 'local' &&
        window.location.origin === 'http://127.0.0.1:5173');

    return {
      token: data?.token || '',
      sessionID: data?.sessionID || '',
      userID: data?.userID || data?.user?.id || '',
      email: data?.user?.email || '',
      position: '',
      name: data?.user?.name || '',
      shortName: data?.user?.shortName || '',
      birthdate: moment(new Date()).format('YYYY-MM-DD'),
      avatar: '',
      roleLevel: data?.user?.roleLevel ?? 6,
      isServoAccount,
    };
  },
);

export const UserSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.info = { ...state.info, ...action.payload };
      state.isLoggedIn = true;
    },
    setLoginInfo: (state, action: PayloadAction<UserLogin>) => {
      state.loginInfo = action.payload;
    },
    removeLoginInfo: (state) => {
      state.loginInfo = {
        token: '',
        isMaintenanceMode: 0,
        systemDialogs: [],
        user: {
          name: '',
          email: '',
          id: '',
        },
        device: {
          verified: false,
          verificationCode: '',
          deviceID: '',
        },
        sessionID: '',
        logInProcess: '',
        verificationCode: '',
      };
    },
    removeUser: () => {
      return initialState;
    },
    setMFALogin: (
      state,
      action: PayloadAction<{
        mfaLoginToken: string;
        loginToken: string;
        data: TLoginRes['data'];
      }>,
    ) => {
      state.mfaLogin.open = true;
      state.mfaLogin.mfaLoginToken = action.payload.mfaLoginToken;
      state.mfaLogin.loginToken = action.payload.loginToken;
      state.mfaLogin.data = action.payload.data;
    },

    setMFAStatus: (state, action: PayloadAction<boolean>) => {
      state.mfaLogin.isMFAEnabled = action.payload;
    },

    setMFALoginLoading: (state, action: PayloadAction<boolean>) => {
      state.mfaLogin.loading = action.payload;
    },

    clearMFALogin: (state) => {
      const isMFAEnabled = state.mfaLogin?.isMFAEnabled;
      state.mfaLogin = {
        open: false,
        mfaLoginToken: '',
        signature: '',
        loading: false,
        loginToken: '',
        data: undefined,
        isMFAEnabled,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveUserThunk.fulfilled, (state, action) => {
      state.info = { ...state.info, ...action.payload };
      state.isLoggedIn = true;
    });
  },
});

/**
 * Redux-persist transform for the `user` slice that sanitizes `mfaLogin` state
 * before it is persisted to storage (on rehydration, the inbound state is
 * left untouched).
 *
 * Purpose:
 * MFA login is a multi-step, transient flow (token exchange, signature,
 * loading state, etc.). None of that should survive a page reload — if it
 * did, a stale `mfaLoginToken`/`signature`/`loginToken` could be rehydrated
 * into a fresh session and cause incorrect MFA state or stuck loading flags.
 *
 * Behavior:
 * - `open`, `mfaLoginToken`, `signature`, `loading`, `loginToken`, `data`
 *   are all reset to their empty/default values on persist (outbound).
 * - `isMFAEnabled` is the one field that IS preserved across reloads,
 *   since whether the user has MFA enabled is not transient — it falls
 *   back to `false` only if it was never set.
 * - `whitelist: ['user']` scopes this transform to only run against the
 *   `user` slice, not the entire persisted root state.
 *
 * Registered in the redux-persist config's `transforms` array for the
 * `user` reducer.
 */
export const mfaLoginResetTransform = createTransform<UserState, UserState>(
  (inboundState) => inboundState,
  (outboundState) => ({
    ...outboundState,
    mfaLogin: {
      open: false,
      mfaLoginToken: '',
      signature: '',
      loading: false,
      loginToken: '',
      data: undefined,
      isMFAEnabled: outboundState.mfaLogin?.isMFAEnabled ?? false,
    },
  }),
  { whitelist: ['user'] },
);

export default UserSlice.reducer;
export const {
  setUser,
  removeUser,
  setLoginInfo,
  removeLoginInfo,
  setMFALogin,
  setMFAStatus,
  setMFALoginLoading,
  clearMFALogin,
} = UserSlice.actions;
